import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronsDownUp, ChevronsUpDown, Landmark } from "lucide-react";
import { EmptyState, ErrorState, LoadingState, TableShell, TH } from "@/components/PageUi";
import { InfoHint } from "@/components/InfoHint";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { apiErrorMessage } from "@/lib/api-error";
import { formatCalendarDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useBooks } from "./BooksProvider";
import { useLoad } from "./useLoad";
import { BooksPeriodScreen, money, num } from "./shared";
import { usePeriodLink, type Period } from "./period";
import {
  ExportButton,
  MAX_MONTHS,
  MONTH_TABLE,
  MonthBars,
  PINNED_CELL,
  SOLID_ROW,
  STICKY_CELL,
  csvName,
  downloadCsv,
  drillKey,
  monthColumnLabel,
  useDrilldown,
  useScrollToEnd,
  type DrillTarget,
} from "./ReportKit";
import type { Book, CashFlowCategory } from "./types";

/** What the money was for, in the order a reader thinks of it: trading, then tax, then the balance sheet. */
const CATEGORIES: Array<{ key: CashFlowCategory; label: string; hint?: string }> = [
  { key: "CustomerReceipts", label: "Customer receipts", hint: "Invoices paid by customers." },
  { key: "Income", label: "Other income", hint: "Income received straight into the bank, such as interest." },
  { key: "SupplierPayments", label: "Supplier payments", hint: "Bills paid to suppliers." },
  { key: "Expenses", label: "Expenses", hint: "Costs paid straight from the bank: card spend, payroll, fees." },
  { key: "Tax", label: "VAT" },
  { key: "Financing", label: "Financing", hint: "Loans, director's loans and money put in or taken out by the owners." },
  { key: "Assets", label: "Assets bought or sold" },
  { key: "Transfers", label: "Transfers", hint: "Between your own bank accounts. They cancel out across the business." },
  { key: "Uncategorised", label: "Uncategorised", hint: "Bank transactions not yet matched or categorised. Click a figure to review them." },
  { key: "CardPayouts", label: "Card payouts", hint: "Card payments from customers, paid out by Stripe net of its fees." },
  { key: "Other", label: "Other", hint: "Anything that fits none of the above." },
];

/**
 * Cash flow analysis, after Mercury's: each month's starting balance, what moved it (by what the money was for) and
 * where it ended, per bank account. The books' figures, so a month only adds up once its transactions are reviewed.
 */
export function CashFlowReport() {
  return (
    <BooksPeriodScreen
      basis
      title="Cash flow analysis"
      subtitle="Where each month's cash came from and went, and each bank account's balance month by month."
    >
      {(book, period) => <CashFlowBody book={book} period={period} />}
    </BooksPeriodScreen>
  );
}

function CashFlowBody({ book, period }: { book: Book; period: Period }) {
  const { books } = useBooks();
  const withPeriod = usePeriodLink();
  const drill = useDrilldown(period);
  const [collapsed, setCollapsed] = useState(false);
  const flow = useLoad(
    () => books.GET("/v1/books/current/cash-flow", { params: { query: { From: period.fromDate, To: period.asAt, Basis: period.basis } } }),
    [books, period.fromDate, period.asAt, period.basis],
  );
  const scroller = useScrollToEnd<HTMLDivElement>(flow.data);

  if (flow.loading && !flow.data) return <LoadingState label="Loading the cash flow" />;
  if (flow.error || !flow.data) return <ErrorState message={apiErrorMessage(flow.error, "Couldn't load the cash flow.")} onRetry={flow.reload} />;
  const months = flow.data.months ?? [];
  if (months.length > MAX_MONTHS)
    return <EmptyState icon={Landmark} title={`Pick ${MAX_MONTHS} months or fewer`}>The analysis has a column per month.</EmptyState>;
  const accounts = months[0]?.accounts ?? [];
  if (accounts.length === 0) return <EmptyState icon={Landmark} title="No bank accounts in the books" />;

  type Col = (typeof months)[number] & { label: string; ym: string };
  const cols: Col[] = months.map((m) => {
    const ym = (m.month ?? "").slice(0, 7);
    return { ...m, ym, label: monthColumnLabel(ym, period.asAt) };
  });
  const last = cols.length - 1;
  // The latest month stays pinned on the right; `solid` for the header and total rows.
  const pin = (ci: number, solid = false) => ci === last && cn(PINNED_CELL, solid && "bg-muted");
  const category = (m: Col, key: CashFlowCategory) => num(m.byCategory?.find((c) => c.category === key)?.amount);
  const used = CATEGORIES.filter((c) => cols.some((m) => category(m, c.key) !== 0));
  const accountOf = (m: Col, id: string) => m.accounts?.find((a) => a.ledgerAccountId === id);
  const name = (a: (typeof accounts)[number]) => (a.code ? `${a.code} · ${a.name}` : (a.name ?? ""));
  const bankAccountOf = new Map((book.bankAccounts ?? []).map((b) => [b.ledgerAccountId, b.bankAccountId ?? undefined]));

  // An ending balance opens everything up to the month's last day; a month's movement opens that month's lines.
  const endTargets: DrillTarget[] = accounts.flatMap((a) =>
    cols.map((m) => ({
      accountId: a.ledgerAccountId!,
      bankAccountId: bankAccountOf.get(a.ledgerAccountId),
      label: name(a),
      to: m.to!,
      amount: num(accountOf(m, a.ledgerAccountId!)?.closing),
      natural: "debit" as const,
      caption: `Balance at ${formatCalendarDate(m.to!)}`,
    })),
  );
  const moveTargets: DrillTarget[] = accounts.flatMap((a) =>
    cols.map((m) => ({
      accountId: a.ledgerAccountId!,
      bankAccountId: bankAccountOf.get(a.ledgerAccountId),
      label: name(a),
      from: m.from!,
      to: m.to!,
      amount: num(accountOf(m, a.ledgerAccountId!)?.net),
      natural: "debit" as const,
      caption: `Movement in ${m.label}`,
    })),
  );
  const cell = (targets: DrillTarget[], ai: number, ci: number, value: number, tone = false) => {
    const index = ai * cols.length + ci;
    return (
      <TableCell key={ci} className={cn("p-0 text-right", pin(ci))}>
        <button
          type="button"
          onClick={() => drill.open(targets, index)}
          className={cn(
            "w-full px-3 py-2 text-right tabular-nums transition hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none",
            drill.openKey === drillKey(targets[index]!) && "bg-accent-blue/10",
            tone && value < 0 && "text-rose-700 dark:text-rose-400",
            value === 0 && "text-muted-foreground/60",
          )}
        >
          {money(value)}
        </button>
      </TableCell>
    );
  };
  const totalRow = (label: string, values: number[], tone = false) => (
    <TableRow className={SOLID_ROW}>
      <TableCell className={cn(STICKY_CELL, "bg-muted font-semibold")}>{label}</TableCell>
      {values.map((v, ci) => (
        <TableCell key={ci} className={cn("px-3 text-right font-semibold tabular-nums", pin(ci, true), tone && v < 0 && "text-rose-700 dark:text-rose-400")}>
          {money(v)}
        </TableCell>
      ))}
    </TableRow>
  );
  const accountRows = (key: string, render: (a: (typeof accounts)[number], ai: number) => React.ReactNode) =>
    collapsed
      ? null
      : accounts.map((a, ai) => (
          <TableRow key={`${key}-${a.ledgerAccountId}`} className="hover:bg-transparent">
            <TableCell className={cn(STICKY_CELL, "max-w-64 truncate pl-6 text-muted-foreground")}>{name(a)}</TableCell>
            {render(a, ai)}
          </TableRow>
        ));

  const exportCsv = () =>
    downloadCsv(csvName("cash-flow", period), [
      ["", ...cols.map((c) => c.label)],
      ["Starting balance", ...cols.map((m) => num(m.opening))],
      ...accounts.map((a) => [name(a), ...cols.map((m) => num(accountOf(m, a.ledgerAccountId!)?.opening))]),
      ...used.map((c) => [c.label, ...cols.map((m) => category(m, c.key))]),
      ["Net cash flow", ...cols.map((m) => num(m.net))],
      ...accounts.map((a) => [`${name(a)} movement`, ...cols.map((m) => num(accountOf(m, a.ledgerAccountId!)?.net))]),
      ["Ending balance", ...cols.map((m) => num(m.closing))],
      ...accounts.map((a) => [name(a), ...cols.map((m) => num(accountOf(m, a.ledgerAccountId!)?.closing))]),
    ]);

  const net = cols.reduce((s, m) => s + num(m.net), 0);
  return (
    <div className="space-y-5">
      <div className="grid gap-6 @3xl:grid-cols-2">
        <MonthBars
          labels={cols.map((c) => c.label)}
          series={[{ label: "Cash at month end", className: "bg-accent-blue/50", values: cols.map((m) => num(m.closing)) }]}
          summary={`${money(cols[last]?.closing)} at ${formatCalendarDate(period.asAt)}`}
          heightClass="h-16 @3xl:h-[132px]"
          openLast={period.asAt < period.toDate}
        />
        <MonthBars
          labels={cols.map((c) => c.label)}
          series={[
            { label: "Money in", className: "bg-emerald-500/60", values: cols.map((m) => num(m.moneyIn)) },
            { label: "Money out", className: "bg-rose-500/50", values: cols.map((m) => num(m.moneyOut)) },
          ]}
          summary={`Net ${net > 0 ? "+" : ""}${money(net)}`}
          heightClass="h-16 @3xl:h-[132px]"
          openLast={period.asAt < period.toDate}
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">
          The books' balances. To check them against the bank, see{" "}
          <Link to={withPeriod("/books")} className="font-medium text-foreground underline-offset-4 hover:underline">
            Books home
          </Link>
          .
        </p>
        <div className="flex shrink-0 items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setCollapsed((c) => !c)}>
            {collapsed ? <ChevronsUpDown className="size-4" /> : <ChevronsDownUp className="size-4" />}
            {collapsed ? "Show accounts" : "Totals only"}
          </Button>
          <ExportButton onExport={exportCsv} />
        </div>
      </div>

      <div ref={scroller}>
        <TableShell className={MONTH_TABLE}>
          <Table>
            <TableHeader>
              <TableRow className={SOLID_ROW}>
                <TableHead className={cn(TH, STICKY_CELL, "min-w-48 bg-muted")}>Cash</TableHead>
                {cols.map((c, ci) => (
                  <TableHead key={c.ym} className={cn(TH, "min-w-28 text-right", pin(ci, true))}>
                    {c.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {totalRow("Starting balance", cols.map((m) => num(m.opening)))}
              {accountRows("open", (a) =>
                cols.map((m, ci) => (
                  // A starting balance is the previous month's ending balance, which opens from there.
                  <TableCell key={ci} className={cn("px-3 text-right tabular-nums text-muted-foreground", pin(ci))}>
                    {money(accountOf(m, a.ledgerAccountId!)?.opening)}
                  </TableCell>
                )),
              )}

              <TableRow className={SOLID_ROW}>
                <TableCell className={cn(STICKY_CELL, "bg-muted text-xs font-semibold uppercase tracking-wide text-muted-foreground")}>
                  Movement
                </TableCell>
                {last > 0 ? <TableCell colSpan={last} /> : null}
                <TableCell className={cn(pin(last, true))} />
              </TableRow>
              {used.map((c) => (
                <TableRow key={c.key} className="hover:bg-transparent">
                  <TableCell className={cn(STICKY_CELL, "pl-6")}>
                    <span className="inline-flex items-center gap-1">
                      {c.label}
                      {c.hint ? <InfoHint label={`About ${c.label}`}>{c.hint}</InfoHint> : null}
                    </span>
                  </TableCell>
                  {cols.map((m, ci) => {
                    const v = category(m, c.key);
                    return (
                      <TableCell
                        key={ci}
                        className={cn("px-3 text-right tabular-nums", pin(ci), v < 0 && "text-rose-700 dark:text-rose-400", v === 0 && "text-muted-foreground/60")}
                      >
                        {c.key === "Uncategorised" && v !== 0 ? (
                          <Link
                            to={`/books/bank-lines?${new URLSearchParams({ status: "Uncategorised", from: m.ym, to: m.ym })}`}
                            className="underline decoration-dotted underline-offset-4 hover:decoration-solid"
                          >
                            {money(v)}
                          </Link>
                        ) : (
                          money(v)
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
              {totalRow("Net cash flow", cols.map((m) => num(m.net)), true)}
              {accountRows("move", (a, ai) => cols.map((m, ci) => cell(moveTargets, ai, ci, num(accountOf(m, a.ledgerAccountId!)?.net), true)))}

              {totalRow("Ending balance", cols.map((m) => num(m.closing)))}
              {accountRows("close", (a, ai) => cols.map((m, ci) => cell(endTargets, ai, ci, num(accountOf(m, a.ledgerAccountId!)?.closing))))}
            </TableBody>
          </Table>
        </TableShell>
      </div>
      {drill.sheet}
    </div>
  );
}
